/**
 * The unified map's playback bar view model (#327), for both modes: the
 * radar's observed frames and the forecast's valid times. It mirrors what the
 * two timelines compute today — the radar control (local hour only) and the
 * forecast playback bar (local date + hour, UTC day + hour beneath).
 *
 * Times are epoch SECONDS in both feeds: RainViewer `past[].time` for the
 * radar, DRF `DATETIME_FORMAT="%s"` for forecasts (sometimes as a string,
 * hence `Number()`). No i18n here: `sliderLabel` names the message for the
 * caller to translate.
 */
import * as Dates from '../Dates';
import type {DateTimeConfig} from '../Dates';
import {formatForecastTimeLabel} from '../ForecastMap/formatForecastTimeLabel';

export type MapTimelineFrameTime = number | string;

export type MapTimelineView = {
  /** Slider value: the current frame, clamped onto the frames. */
  value: number;
  max: number;
  /** A slider needs at least two frames. */
  playable: boolean;
  /** Local time of the current frame; '' without one. */
  time: string;
  /** Forecast only: the same time in UTC (weekday + hour). */
  utcTime: string | null;
  /** The slider's accessible name: `weatherMap.radarTime` / `forecastMaps.frameTime`. */
  sliderLabel: 'radarTime' | 'frameTime';
};

/**
 * Index of the frame closest to `target` (epoch seconds); the earlier frame
 * wins a tie, as the radar's "Now" button does. -1 when no frame has a time.
 */
export const closestFrameIndex = (
  frames: readonly MapTimelineFrameTime[],
  target: number,
): number => {
  let closest = -1;
  let closestDiff = Infinity;
  frames.forEach((frame, index) => {
    const diff = Math.abs(Number(frame) - target);
    if (diff < closestDiff) {
      closest = index;
      closestDiff = diff;
    }
  });
  return closest;
};

export const mapTimelineView = ({
  kind,
  frames,
  index,
  timezone,
  dateTimeConfig,
}: {
  kind: 'observed' | 'forecast';
  frames: readonly MapTimelineFrameTime[];
  index: number;
  timezone: string;
  dateTimeConfig: DateTimeConfig;
}): MapTimelineView => {
  const max = Math.max(frames.length - 1, 0);
  // A NaN index would survive both clamps (Math.max(NaN, 0) is NaN).
  const value = Math.min(Math.max(Number.isFinite(index) ? index : 0, 0), max);
  const frame = frames[value];
  const playable = frames.length >= 2;
  const hour = Dates.hourFormatter(dateTimeConfig);

  if (kind === 'observed') {
    const seconds = Number(frame);
    return {
      value,
      max,
      playable,
      time: seconds ? Dates.formatDate(seconds, timezone, hour) : '',
      utcTime: null,
      sliderLabel: 'radarTime',
    };
  }

  if (frame === undefined) {
    return {
      value,
      max,
      playable,
      time: '',
      utcTime: null,
      sliderLabel: 'frameTime',
    };
  }
  return {
    value,
    max,
    playable,
    time: formatForecastTimeLabel(frame, timezone, dateTimeConfig),
    // Weekday + hour: the UTC day can differ from the local one.
    utcTime: formatForecastTimeLabel(
      frame,
      'UTC',
      dateTimeConfig,
      `ddd ${hour}`,
    ),
    sliderLabel: 'frameTime',
  };
};
